'use client'

import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { cn } from '@tebuto/lib/utils'
import ExtraSmallText from '@tebuto/typography/ExtraSmallText'
import LargeText from '@tebuto/typography/LargeText'
import SmallText from '@tebuto/typography/SmallText'
import { ReactNode } from 'react'

export type ModalSize = 'sm' | 'md' | 'lg'

const SIZE_CLASS: Record<ModalSize, string> = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-2xl',
    lg: 'sm:max-w-4xl'
}

export interface PlainModalProps {
    isOpen: boolean
    title: string
    subTitle?: string
    description?: string
    /** Preferred close handler (alias of the deprecated `closeHandler`/`onModalClose`). */
    onClose?: () => void
    /** Max-width preset; when unset the width comes from `className`/content. */
    size?: ModalSize
    verticalPadding?: boolean
    headerBorder?: boolean
    className?: string
    children: ReactNode | ReactNode[]
    /** @deprecated use `onClose` */
    closeHandler?: () => void
    /** @deprecated use `onClose` */
    onModalClose?: () => void
    /** When false, the panel body does not scroll; children can use their own scroll regions (e.g. sticky footer). Default true. */
    bodyScroll?: boolean
    /**
     * When false (and `bodyScroll` is false), the body does not grow with `flex-1`, so short content stays compact instead of leaving a tall empty band.
     * Ignored when `bodyScroll` is true (scrolling layouts still need a bounded flex region). Default true.
     */
    bodyFillsRemainingSpace?: boolean
    /** When true, backdrop click and Escape do not close the dialog; the header close button still calls `closeHandler`. */
    backdropDismissDisabled?: boolean
    'data-testid'?: string
}

export default function PlainModal({
    isOpen,
    title,
    subTitle,
    description,
    className = '',
    children,
    onClose,
    size,
    closeHandler,
    onModalClose = onClose ?? closeHandler,
    verticalPadding = true,
    headerBorder = true,
    bodyScroll = true,
    bodyFillsRemainingSpace = true,
    backdropDismissDisabled = false,
    'data-testid': testId
}: PlainModalProps) {
    const { t } = useComponentTranslation()
    const handleDialogClose = backdropDismissDisabled ? () => {} : (onModalClose ?? (() => {}))
    const bodyShouldFillHeight = bodyScroll || bodyFillsRemainingSpace
    return (
        <Transition appear show={isOpen}>
            <Dialog as="div" className="relative z-50 min-w-0 overflow-x-hidden" onClose={handleDialogClose}>
                <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25" />
                </TransitionChild>
                <div className="fixed inset-0 flex min-w-0 flex-col overflow-hidden">
                    <div className="flex min-h-0 min-w-0 w-full max-w-full flex-1 items-center justify-center p-3 text-center sm:p-4 md:py-6 md:px-4">
                        <TransitionChild
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel
                                className={cn(
                                    'relative mx-auto flex min-h-0 min-w-0 w-full max-w-full flex-col overflow-x-hidden overflow-y-hidden bg-white text-left align-middle shadow-xl transition-all md:rounded-2xl',
                                    'max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[calc(100dvh-3rem)]',
                                    size && SIZE_CLASS[size],
                                    className
                                )}
                                data-testid={testId}
                            >
                                {onModalClose ? (
                                    <button
                                        type="button"
                                        className={cn(
                                            'absolute right-4 top-4 z-20 rounded-lg p-1 text-foreground-tertiary',
                                            'transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-brand/50'
                                        )}
                                        onClick={onModalClose}
                                        aria-label={t('modals.closeDialog')}
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                ) : null}
                                <DialogTitle
                                    className={cn(
                                        'min-w-0 max-w-full shrink-0 pr-14 tracking-tight xl:text-3xl lg:text-xl text-lg px-6 md:px-10 py-4 bg-primary-25',
                                        headerBorder && 'border-b'
                                    )}
                                >
                                    <div>
                                        {subTitle && <SmallText className="mt-0 mb-2">{subTitle}</SmallText>}
                                        <LargeText className="m-0 font-semibold text-foreground-secondary">
                                            {title}
                                        </LargeText>
                                    </div>
                                    {description && (
                                        <ExtraSmallText className="mt-2 m-0 tracking-normal text-foreground-tertiary">
                                            {description}
                                        </ExtraSmallText>
                                    )}
                                </DialogTitle>

                                <div
                                    className={cn(
                                        'flex min-h-0 min-w-0 flex-col overflow-x-hidden',
                                        bodyShouldFillHeight && 'flex-1',
                                        bodyScroll ? 'overflow-y-auto' : 'overflow-y-hidden',
                                        verticalPadding ? 'py-8' : ''
                                    )}
                                >
                                    {children}
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
